import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, PhoneCall, PhoneOff, Mic, Bot, Globe, ListChecks, Save, Loader2, Copy, RefreshCw,
  X, PhoneIncoming, PhoneOutgoing, Settings as SettingsIcon, Volume2, CalendarClock,
} from "lucide-react";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { startVoiceCall } from "../lib/voiceClient";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";
const labelCls = "font-mono text-[10px] uppercase tracking-wider text-[#8696A0]";
const card = "border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4";
const btnPrimary = "inline-flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-2 border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50";

const TABS = [
  { k: "agent", label: "Agent", icon: Bot },
  { k: "test", label: "Test call", icon: Mic },
  { k: "widget", label: "Website button", icon: Globe },
  { k: "phone", label: "Phone & outbound", icon: Phone },
  { k: "automations", label: "Call automations", icon: CalendarClock },
  { k: "logs", label: "Call logs", icon: ListChecks },
];

const errText = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fb;
};
const when = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };
const mmss = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round((s || 0) % 60)).padStart(2, "0")}`;

function Toggle({ on, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${on ? "bg-[#25D366]" : "bg-[#CBD5E1]"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div><p className="text-sm text-[#111B21]">{label}</p>{hint && <p className="text-xs text-[#8696A0]">{hint}</p>}</div>
      {children}
    </div>
  );
}

function CopyField({ label, value, testid }) {
  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className="flex gap-2 mt-1">
        <input readOnly value={value || "—"} className={inputCls + " font-mono text-xs"} data-testid={testid} />
        <button className={btnGhost} onClick={() => { navigator.clipboard.writeText(value || ""); toast.success("Copied"); }}><Copy className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function ProviderPicker({ kind, value, onChange, catalog, defaults }) {
  const list = catalog?.[kind] || {};
  const prov = value?.provider || defaults?.provider || Object.keys(list)[0];
  const meta = list[prov] || { models: [], voices: [] };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <p className={labelCls}>Provider</p>
        <select className={inputCls + " mt-1"} value={prov} data-testid={`voice-${kind}-provider`}
          onChange={(e) => onChange({ ...value, provider: e.target.value, model: (list[e.target.value]?.models || [])[0] || "", voice: (list[e.target.value]?.voices || [])[0] || "" })}>
          {Object.entries(list).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <div>
        <p className={labelCls}>Model</p>
        <select className={inputCls + " mt-1"} value={value?.model || defaults?.model || ""} data-testid={`voice-${kind}-model`}
          onChange={(e) => onChange({ ...value, provider: prov, model: e.target.value })}>
          {(meta.models || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      {kind === "tts" && (
        <div>
          <p className={labelCls}>Voice</p>
          {(meta.voices || []).length ? (
            <select className={inputCls + " mt-1"} value={value?.voice || defaults?.voice || ""} data-testid="voice-tts-voice"
              onChange={(e) => onChange({ ...value, provider: prov, voice: e.target.value })}>
              {meta.voices.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ) : (
            <input className={inputCls + " mt-1 font-mono"} placeholder="Voice ID" value={value?.voice || ""}
              onChange={(e) => onChange({ ...value, provider: prov, voice: e.target.value })} />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- Agent ----------------
function AgentTab({ data, catalog, reload }) {
  const [s, setS] = useState(data.settings);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => setS(data.settings), [data]);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { inbound, widget, outbound, ...rest } = s;
      await api.put("/voice/settings", { settings: rest });
      toast.success("Voice agent saved");
      reload();
    } catch (e) { toast.error(errText(e, "Save failed")); } finally { setSaving(false); }
  };
  const preview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/voice/preview-voice", { text: s.greeting, tts: s.tts }, { responseType: "blob" });
      new Audio(URL.createObjectURL(r.data)).play();
    } catch (e) {
      let msg = "Preview failed";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (err) {}
      toast.error(msg);
    } finally { setPreviewing(false); }
  };
  return (
    <div className="space-y-6 max-w-3xl">
      {!data.voice_allowed && (
        <div className="border border-amber-300 bg-amber-50 text-amber-800 rounded-lg p-4 text-sm">Voice calls are switched off for this workspace by the platform admin.</div>
      )}
      <div className={card}>
        <Row label="Voice agent on" hint="When off, no calls are answered or placed (web, website button or phone).">
          <Toggle on={!!s.enabled} onChange={(v) => set("enabled", v)} testid="voice-enabled" />
        </Row>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><p className={labelCls}>Agent name</p><input className={inputCls + " mt-1"} value={s.agent_name || ""} onChange={(e) => set("agent_name", e.target.value)} data-testid="voice-agent-name" /></div>
          <div>
            <p className={labelCls}>Agent is</p>
            <select className={inputCls + " mt-1"} value={s.agent_gender || "female"} onChange={(e) => set("agent_gender", e.target.value)} data-testid="voice-agent-gender">
              <option value="female">Female (bol rahi hoon)</option>
              <option value="male">Male (bol raha hoon)</option>
            </select>
          </div>
          <div>
            <p className={labelCls}>Caller language</p>
            <select className={inputCls + " mt-1"} value={s.language || "unknown"} onChange={(e) => set("language", e.target.value)} data-testid="voice-language">
              {Object.entries(catalog.languages || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        <div>
          <p className={labelCls}>Greeting (first thing the agent says)</p>
          <textarea rows={2} className={inputCls + " mt-1"} value={s.greeting || ""} onChange={(e) => set("greeting", e.target.value)} data-testid="voice-greeting" />
        </div>
        <Row label="Use the AI Agent's rules, prices and knowledge base" hint="Same source as WhatsApp, so prices never differ between chat and calls.">
          <Toggle on={s.use_ai_agent_rules !== false} onChange={(v) => set("use_ai_agent_rules", v)} />
        </Row>
        <Row label="After the call: send payment details on WhatsApp" hint="Only when the caller showed interest. Uses the verified price, your advance % and payment link, then starts the follow-up automation (if automations are on).">
          <Toggle on={s.post_call_whatsapp !== false} onChange={(v) => set("post_call_whatsapp", v)} testid="voice-post-call-wa" />
        </Row>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2"><p className={labelCls}>Payment link</p><input className={inputCls + " mt-1 font-mono"} placeholder="razorpay.me/@yourbusiness" value={s.payment_link || ""} onChange={(e) => set("payment_link", e.target.value)} data-testid="voice-payment-link" /></div>
          <div><p className={labelCls}>Advance %</p><input type="number" min="0" max="100" className={inputCls + " mt-1"} value={s.advance_percent ?? 50} onChange={(e) => set("advance_percent", Number(e.target.value))} /></div>
        </div>
        <div>
          <p className={labelCls}>Extra instructions for calls (optional)</p>
          <textarea rows={4} className={inputCls + " mt-1"} placeholder="e.g. Always offer the 4-Bhajan Pack first to YouTube channel owners." value={s.instructions || ""} onChange={(e) => set("instructions", e.target.value)} />
        </div>
      </div>

      <div className={card}>
        <h3 className="font-heading text-sm">Voice</h3>
        <ProviderPicker kind="tts" value={s.tts} defaults={catalog.defaults?.tts} catalog={catalog} onChange={(v) => set("tts", v)} />
        <div className="flex items-center gap-3">
          <p className={labelCls}>Speed</p>
          <input type="range" min="0.7" max="1.4" step="0.05" value={s.tts?.pace || 1} onChange={(e) => set("tts", { ...s.tts, pace: Number(e.target.value) })} />
          <span className="text-xs font-mono">{(s.tts?.pace || 1).toFixed(2)}×</span>
          <button className={btnGhost + " ml-auto"} onClick={preview} disabled={previewing} data-testid="voice-preview">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />} Hear greeting
          </button>
        </div>
        <h3 className="font-heading text-sm pt-2">Hearing (speech-to-text)</h3>
        <ProviderPicker kind="stt" value={s.stt} defaults={catalog.defaults?.stt} catalog={catalog} onChange={(v) => set("stt", v)} />
        <h3 className="font-heading text-sm pt-2">Brain (reply model)</h3>
        <ProviderPicker kind="llm" value={s.llm} defaults={catalog.defaults?.llm} catalog={catalog} onChange={(v) => set("llm", v)} />
        <p className="text-xs text-[#8696A0]">Keys: your own keys in Settings → Voice &amp; Calling are used first; otherwise the platform's keys.</p>
      </div>

      <div className={card}>
        <h3 className="font-heading text-sm">Call behaviour</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><p className={labelCls}>Wait after caller stops (ms)</p><input type="number" className={inputCls + " mt-1"} value={s.end_silence_ms} onChange={(e) => set("end_silence_ms", Number(e.target.value))} /></div>
          <div><p className={labelCls}>Max call length (sec)</p><input type="number" className={inputCls + " mt-1"} value={s.max_call_seconds} onChange={(e) => set("max_call_seconds", Number(e.target.value))} /></div>
          <div><p className={labelCls}>"Are you there?" after (sec)</p><input type="number" className={inputCls + " mt-1"} value={s.idle_prompt_seconds} onChange={(e) => set("idle_prompt_seconds", Number(e.target.value))} /></div>
        </div>
        <Row label="Record calls" hint={catalog.limits?.recording_allowed === false ? "Recording is disabled by the admin." : "Tell callers if your policy requires it."}>
          <Toggle on={!!s.recording} onChange={(v) => set("recording", v)} />
        </Row>
        <Row label="Save callers as leads" hint="Name, number, service, price and a summary go to Leads after every call.">
          <Toggle on={s.create_leads !== false} onChange={(v) => set("create_leads", v)} />
        </Row>
      </div>
      <button className={btnPrimary} onClick={save} disabled={saving} data-testid="voice-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save voice agent
      </button>
    </div>
  );
}

// ---------------- Test call ----------------
function TestTab({ data }) {
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState("");
  const [lines, setLines] = useState([]);
  const [level, setLevel] = useState(0);
  const call = useRef(null);
  useEffect(() => () => call.current?.stop(), []);
  const start = async () => {
    setBusy(true); setLines([]);
    try {
      const r = await api.post("/voice/test-session");
      call.current = await startVoiceCall(r.data.ws_url, {
        onEvent: (m) => {
          if (m.type === "transcript") setLines((l) => [...l, { role: m.role, text: m.text }]);
          if (m.type === "state") setState(m.state);
          if (m.type === "error") toast.error(m.message);
        },
        onLevel: (v) => setLevel(v),
        onClose: () => { setLive(false); setState(""); },
      });
      setLive(true);
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Allow the microphone to test." : errText(e, "Could not start the test call"));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 max-w-2xl">
      {!data.settings.enabled && <div className="border border-amber-300 bg-amber-50 text-amber-800 rounded-lg p-3 text-sm">The agent is off — a test call still works so you can try it before going live.</div>}
      <div className={card}>
        <p className="text-sm text-[#54656F]">Talk to your agent from this browser exactly as a caller would. Uses your microphone; the call is logged as a test.</p>
        <div className="flex items-center gap-3">
          {!live ? (
            <button className={btnPrimary} onClick={start} disabled={busy} data-testid="voice-test-start">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />} Start test call
            </button>
          ) : (
            <button className="inline-flex items-center gap-2 bg-red-500 text-white hover:bg-red-600 rounded-lg px-4 py-2 text-sm font-medium" onClick={() => call.current?.stop()} data-testid="voice-test-end">
              <PhoneOff className="h-4 w-4" /> End call
            </button>
          )}
          {live && (
            <>
              <div className="h-2 w-32 bg-[#E9EDEF] rounded-full overflow-hidden"><div className="h-full bg-[#25D366]" style={{ width: `${Math.min(100, level * 900)}%` }} /></div>
              <span className="text-xs font-mono text-[#8696A0]">{state}</span>
            </>
          )}
        </div>
      </div>
      <div className={card + " min-h-[200px]"} data-testid="voice-test-transcript">
        {lines.length === 0 && <p className="text-sm text-[#8696A0]">Transcript appears here.</p>}
        {lines.map((l, i) => (
          <div key={i} className={`flex ${l.role === "user" ? "justify-end" : ""}`}>
            <p className={`text-sm rounded-lg px-3 py-2 max-w-[80%] ${l.role === "user" ? "bg-[#D9FDD3]" : "bg-[#F0F2F5]"}`}>{l.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Website button ----------------
function WidgetTab({ data, reload }) {
  const [w, setW] = useState(data.settings.widget);
  const [saving, setSaving] = useState(false);
  useEffect(() => setW(data.settings.widget), [data]);
  const snippet = `<script src="${data.urls.widget_script}" data-token="${w.token}" data-label="${w.button_text || "Talk to us"}" data-color="${w.color || "#25D366"}" async></script>`;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/voice/settings", { settings: { widget: { ...w, allowed_origins: (w.allowed_origins || []).filter(Boolean) } } });
      toast.success("Website button saved"); reload();
    } catch (e) { toast.error(errText(e, "Save failed")); } finally { setSaving(false); }
  };
  const rotate = async () => {
    if (!window.confirm("Create a new code? The old snippet stops working on every website.")) return;
    await api.post("/voice/widget/rotate"); toast.success("New code created — update your website"); reload();
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <div className={card}>
        <Row label="Website call button" hint="A floating button on your website; visitors talk to the agent from their browser (no phone charges).">
          <Toggle on={!!w.enabled} onChange={(v) => setW({ ...w, enabled: v })} testid="voice-widget-enabled" />
        </Row>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><p className={labelCls}>Button text</p><input className={inputCls + " mt-1"} value={w.button_text || ""} onChange={(e) => setW({ ...w, button_text: e.target.value })} /></div>
          <div><p className={labelCls}>Button colour</p><input type="color" className="mt-1 h-9 w-20 rounded" value={w.color || "#25D366"} onChange={(e) => setW({ ...w, color: e.target.value })} /></div>
        </div>
        <div>
          <p className={labelCls}>Allowed websites (one per line; empty = any)</p>
          <textarea rows={3} className={inputCls + " mt-1 font-mono text-xs"} placeholder={"muviarts.in\nwww.muviarts.in"}
            value={(w.allowed_origins || []).join("\n")} onChange={(e) => setW({ ...w, allowed_origins: e.target.value.split("\n").map((x) => x.trim()) })} data-testid="voice-widget-origins" />
        </div>
        <div className="flex gap-2">
          <button className={btnPrimary} onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
          <button className={btnGhost} onClick={rotate}><RefreshCw className="h-4 w-4" /> New code</button>
        </div>
      </div>
      <div className={card}>
        <p className="text-sm text-[#54656F]">Paste this just before <code>&lt;/body&gt;</code> on your website:</p>
        <CopyField label="Embed code" value={snippet} testid="voice-widget-snippet" />
        {!data.settings.enabled && <p className="text-xs text-amber-700">The agent is off — the button won't connect until you turn it on in Agent.</p>}
      </div>
    </div>
  );
}

// ---------------- Phone ----------------
function PhoneTab({ data, reload }) {
  const navigate = useNavigate();
  const [tel, setTel] = useState(null);
  const [o, setO] = useState(data.settings.outbound);
  const [inb, setInb] = useState(data.settings.inbound?.enabled !== false);
  const [to, setTo] = useState("");
  const [leadId, setLeadId] = useState("");
  const [leads, setLeads] = useState([]);
  const [consent, setConsent] = useState(false);
  const [calling, setCalling] = useState(false);
  useEffect(() => { setO(data.settings.outbound); }, [data]);
  useEffect(() => {
    api.get("/voice/telephony").then((r) => setTel(r.data)).catch(() => setTel({}));
    api.get("/leads").then((r) => setLeads((Array.isArray(r.data) ? r.data : r.data.leads || []).filter((l) => l.phone && !l.is_test).slice(0, 300))).catch(() => {});
  }, []);
  const save = async () => {
    try {
      await api.put("/voice/settings", { settings: { outbound: { ...o, test_numbers: (o.test_numbers || []).map((x) => x.trim()).filter(Boolean) } } });
      toast.success("Phone settings saved"); reload();
    } catch (e) { toast.error(errText(e, "Save failed")); }
  };
  const call = async () => {
    setCalling(true);
    try {
      await api.post("/voice/calls/outbound", { to, lead_id: leadId || null, consent });
      toast.success("Calling… see Call logs"); setConsent(false);
    } catch (e) { toast.error(errText(e, "Call failed")); } finally { setCalling(false); }
  };
  const prov = tel?.provider;
  return (
    <div className="space-y-4 max-w-3xl">
      <div className={card}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#111B21]">Phone connection</p>
            <p className="text-xs text-[#8696A0]">{prov ? `${prov[0].toUpperCase() + prov.slice(1)} · ${tel.connected ? "connected" : "not verified"} · ${(tel.numbers || []).join(", ") || "no number"}` : "Not connected"}</p>
          </div>
          <button className={btnGhost} onClick={() => navigate("/settings/voice")}><SettingsIcon className="h-4 w-4" /> {prov ? "Edit" : "Connect"}</button>
        </div>
      </div>

      <div className={card}>
        <Row label="Answer incoming calls" hint="Calls to your number are answered by the agent.">
          <Toggle on={inb} onChange={async (v) => {
            setInb(v);
            try { await api.put("/voice/settings", { settings: { inbound: { enabled: v } } }); toast.success(v ? "Incoming calls will be answered" : "Incoming calls will not be answered"); }
            catch (e) { toast.error(errText(e, "Save failed")); setInb(!v); }
          }} testid="voice-inbound-enabled" />
        </Row>
        <p className="text-xs text-[#54656F]">Point your phone number at the agent (paste in your provider's console):</p>
        {(!prov || prov === "twilio") && <CopyField label="Twilio → Phone number → Voice 'A call comes in' (Webhook, POST)" value={data.urls.twilio_voice_webhook} testid="voice-url-twilio" />}
        {(!prov || prov === "plivo") && <CopyField label="Plivo → Application → Answer URL (POST)" value={data.urls.plivo_answer_url} />}
        {(!prov || prov === "vobiz") && <CopyField label="Vobiz → Application → Answer URL (POST)" value={data.urls.vobiz_answer_url} />}
        {(!prov || prov === "exotel") && <CopyField label="Exotel → App Bazaar flow → Voicebot applet URL" value={data.urls.exotel_voicebot_url} />}
        <p className="text-xs text-[#8696A0]">These links contain a private key — don't share them.</p>
      </div>

      <div className={card}>
        <Row label="Outbound calls" hint="Let the agent call leads (only people who agreed to be called).">
          <Toggle on={!!o.enabled} onChange={(v) => setO({ ...o, enabled: v })} testid="voice-outbound-enabled" />
        </Row>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2"><p className={labelCls}>Call from number</p><input className={inputCls + " mt-1 font-mono"} placeholder={(tel?.numbers || [])[0] || "+91…"} value={o.from_number || ""} onChange={(e) => setO({ ...o, from_number: e.target.value })} /></div>
          <div><p className={labelCls}>From (IST)</p><input type="time" className={inputCls + " mt-1"} value={o.hours?.[0] || "10:00"} onChange={(e) => setO({ ...o, hours: [e.target.value, o.hours?.[1] || "20:00"] })} /></div>
          <div><p className={labelCls}>Until (IST)</p><input type="time" className={inputCls + " mt-1"} value={o.hours?.[1] || "20:00"} onChange={(e) => setO({ ...o, hours: [o.hours?.[0] || "10:00", e.target.value] })} /></div>
          <div><p className={labelCls}>Max calls / day</p><input type="number" className={inputCls + " mt-1"} value={o.daily_limit || 0} onChange={(e) => setO({ ...o, daily_limit: Number(e.target.value) })} /></div>
        </div>
        <div className={"rounded-lg p-3 space-y-2 " + (o.test_only !== false ? "bg-amber-50 border border-amber-300" : "bg-red-50 border border-red-300")} data-testid="voice-outbound-testmode">
          <Row label={o.test_only !== false ? "Test mode ON — only the test numbers below can be called" : "Test mode OFF — the agent can call real leads"}
               hint="Keep this on until calls are fully checked. Real leads are blocked by the server while it is on.">
            <Toggle on={o.test_only !== false} onChange={(v) => {
              if (!v && !window.confirm("Switch off test mode? The agent will be able to call real leads.")) return;
              setO({ ...o, test_only: v });
            }} testid="voice-outbound-test-only" />
          </Row>
          <p className={labelCls}>Test numbers (one per line)</p>
          <textarea rows={2} className={inputCls + " font-mono"} placeholder="+919137518623" value={(o.test_numbers || []).join("\n")}
            onChange={(e) => setO({ ...o, test_numbers: e.target.value.split("\n") })} />
        </div>
        <button className={btnPrimary} onClick={save}><Save className="h-4 w-4" /> Save outbound settings</button>
      </div>

      <div className={card}>
        <h3 className="font-heading text-sm">Call someone now</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p className={labelCls}>Lead (optional)</p>
            <select className={inputCls + " mt-1"} value={leadId} onChange={(e) => { setLeadId(e.target.value); const l = leads.find((x) => x.lead_id === e.target.value); if (l) setTo(l.phone); }} data-testid="voice-call-lead">
              <option value="">— pick a lead —</option>
              {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{l.name || l.phone} · {l.category || ""} · {l.service || ""}</option>)}
            </select>
          </div>
          <div><p className={labelCls}>Number</p><input className={inputCls + " mt-1 font-mono"} placeholder="+91 98765 43210" value={to} onChange={(e) => setTo(e.target.value)} data-testid="voice-call-number" /></div>
        </div>
        <label className="flex items-start gap-2 text-sm text-[#54656F]">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="voice-call-consent" />
          This person asked us to call / agreed to be called (required by Indian telecom rules).
        </label>
        <button className={btnPrimary} onClick={call} disabled={calling || !to || !consent} data-testid="voice-call-now">
          {calling ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneOutgoing className="h-4 w-4" />} Call now
        </button>
      </div>

      <p className="text-xs text-[#8696A0]">Scheduled calls and Vivaan's automatic calls: <button className="underline" onClick={() => navigate("/voice/automations")}>Call automations</button>.</p>
    </div>
  );
}

// 28 Oct 2026 (RK): schedule a call for Vivaan at a set time, with a brief (calling automations).
const ST_COLOR = { scheduled: "text-[#1E7F4F]", calling: "text-[#1E7F4F]", called: "text-[#54656F]", cancelled: "text-[#8696A0]", failed: "text-red-600", missed: "text-red-600" };
function ScheduleCallCard({ leads }) {
  const [f, setF] = useState({ phone: "", at: "", name: "", note: "", goal: "", consent: false });
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const load = useCallback(() => { api.get("/voice/calls/scheduled").then((r) => setRows(r.data || [])).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/voice/calls/scheduled", f);
      toast.success(`Call scheduled for ${when(r.data.at)}`);
      if (r.data.warning) toast.warning(r.data.warning);
      setF({ phone: "", at: "", name: "", note: "", goal: "", consent: false }); load();
    } catch (e) { toast.error(errText(e, "Could not schedule the call")); } finally { setSaving(false); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this scheduled call?")) return;
    try { await api.delete(`/voice/calls/scheduled/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(errText(e, "Could not cancel")); }
  };
  return (
    <div className={card} data-testid="voice-schedule-card">
      <h3 className="font-heading text-sm flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Schedule a call (Vivaan calls at the time you pick)</h3>
      <p className="text-xs text-[#8696A0]">Only for someone who already contacted MuviArts (a chat or a call). Time is Indian time and must be inside the calling hours. Test mode does not block a call you schedule here; every other check still applies.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <p className={labelCls}>Lead (optional)</p>
          <select className={inputCls + " mt-1"} value="" onChange={(e) => { const l = leads.find((x) => x.lead_id === e.target.value); if (l) setF((x) => ({ ...x, phone: l.phone, name: x.name || l.name || "" })); }}>
            <option value="">— pick a lead —</option>
            {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{l.name || l.phone} · {l.service || ""}</option>)}
          </select>
        </div>
        <div><p className={labelCls}>Number</p><input className={inputCls + " mt-1 font-mono"} placeholder="+91 98765 43210" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="voice-schedule-phone" /></div>
        <div><p className={labelCls}>When (IST)</p><input type="datetime-local" className={inputCls + " mt-1"} value={f.at} onChange={(e) => set("at", e.target.value)} data-testid="voice-schedule-at" /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><p className={labelCls}>Name (optional)</p><input className={inputCls + " mt-1"} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div className="md:col-span-2"><p className={labelCls}>Goal of the call</p><input className={inputCls + " mt-1"} placeholder="e.g. get deity, how many bhajans, audio or video; ask him to send Hi on WhatsApp" value={f.goal} onChange={(e) => set("goal", e.target.value)} data-testid="voice-schedule-goal" /></div>
      </div>
      <div><p className={labelCls}>What we know (Vivaan reads this before the call)</p>
        <textarea rows={3} className={inputCls + " mt-1"} placeholder="e.g. Called on 27 Oct about Diwali bhajans for his YouTube channel, the line dropped. 3 Lakshmi bhajans on the store at Rs 2,499 till 2 Nov." value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="voice-schedule-note" /></div>
      <label className="flex items-start gap-2 text-sm text-[#54656F]">
        <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" data-testid="voice-schedule-consent" />
        This person contacted us and is OK to be called back.
      </label>
      <button className={btnPrimary} onClick={save} disabled={saving || !f.phone || !f.at || !f.goal.trim() || !f.note.trim() || !f.consent} data-testid="voice-schedule-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Schedule call
      </button>
      {rows.length > 0 && (
        <div className="border-t border-[#E9EDEF] pt-3 space-y-2" data-testid="voice-schedule-list">
          {rows.map((r) => (
            <div key={r.schedule_id} className="flex items-start justify-between gap-3 text-sm">
              <div className="min-w-0">
                <p className="text-[#111B21]">{when(r.at)} · <span className="font-mono">+{r.phone}</span>{r.name ? ` · ${r.name}` : ""} · <span className={ST_COLOR[r.status] || ""}>{r.status}</span></p>
                <p className="text-xs text-[#8696A0] truncate">{r.goal}</p>
                {r.error && <p className="text-xs text-red-600">{r.error}</p>}
              </div>
              {r.status === "scheduled" && <button className={btnGhost} onClick={() => cancel(r.schedule_id)}><X className="h-4 w-4" /> Cancel</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 28 Oct 2026 (RK): every calling automation in one place, like the WhatsApp Automations page.
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function CallAutomationsTab({ data, reload }) {
  const [leads, setLeads] = useState([]);
  const [ov, setOv] = useState(null);
  const [lc, setLc] = useState(data.settings.lead_calls || {});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const ob = data.settings.outbound || {};
  const load = useCallback(() => { api.get("/voice/lead-calls").then((r) => setOv(r.data)).catch(() => setOv(null)); }, []);
  useEffect(() => {
    load();
    api.get("/leads").then((r) => setLeads((Array.isArray(r.data) ? r.data : r.data.leads || []).filter((l) => l.phone && !l.is_test).slice(0, 300))).catch(() => {});
  }, [load]);
  useEffect(() => { setLc(data.settings.lead_calls || {}); }, [data]);
  const saveLc = async (patch) => {
    const next = { ...lc, ...patch };
    setLc(next);
    try { await api.put("/voice/settings", { settings: { lead_calls: next } }); toast.success("Saved"); reload(); load(); }
    catch (e) { toast.error(errText(e, "Save failed")); }
  };
  const saveCb = async (v) => {
    try { await api.put("/voice/settings", { settings: { auto_callback: v } }); toast.success(v ? "Redial after a dropped call is ON" : "Redial is OFF"); reload(); }
    catch (e) { toast.error(errText(e, "Save failed")); }
  };
  const runPreview = async () => {
    setBusy(true);
    try { const r = await api.get("/voice/lead-calls", { params: { preview: true } }); setPreview(r.data.would_call || []); }
    catch (e) { toast.error(errText(e, "Preview failed")); } finally { setBusy(false); }
  };
  const testMode = ob.test_only !== false;
  const days = lc.days || [0, 1, 2, 3, 4, 5];
  return (
    <div className="space-y-4 max-w-3xl" data-testid="voice-automations">
      <div className={"rounded-lg p-4 text-sm " + (ob.enabled ? "bg-[#E7FCE8] border border-[#25D366]/40" : "bg-amber-50 border border-amber-300")}>
        <p className="text-[#111B21]">Outbound calling is <b>{ob.enabled ? "ON" : "OFF"}</b> · test mode <b>{testMode ? "ON" : "OFF"}</b> · hours {(ob.hours || ["10:00", "20:00"]).join("–")} IST · max {ob.daily_limit || 0} calls/day</p>
        <p className="text-xs text-[#54656F] mt-1">{testMode ? "While test mode is on, only calls you schedule below can ring real customers. Vivaan's automatic follow-up calls stay paused, and a redial can only reach the test number." : "Test mode is off: the automations below can call real customers."}</p>
      </div>

      <div className="rounded-lg p-4 text-xs text-[#54656F] bg-white border border-[#E9EDEF]" data-testid="voice-courtesy-rules">
        <p className="text-sm text-[#111B21] mb-1">No chasing (applies to every automatic and scheduled call)</p>
        <p>• Declined our call (busy / rejected) or picked up and cut within 15 seconds without speaking → no automatic call for 7 days.</p>
        <p>• Didn't pick up twice in a week → no automatic call for 7 days.</p>
        <p>• Never more than one automatic call to the same number in 20 hours. "Don't call" on a call = never called again.</p>
        <p>• A customer who calls us is always answered. "Call now" by you is not blocked.</p>
      </div>

      <ScheduleCallCard leads={leads} />

      <div className={card} data-testid="voice-lead-calls">
        <Row label="Automatic follow-up calls (Vivaan)" hint="Vivaan calls people who were interested on WhatsApp and then went quiet, with a short brief from the chat. Never someone who said no, paid, or is with you.">
          <Toggle on={!!lc.enabled} onChange={(v) => {
            if (v && !window.confirm("Let Vivaan call quiet leads automatically?" + (testMode ? " (Test mode is ON, so these calls stay paused until you switch test mode off.)" : ""))) return;
            saveLc({ enabled: v });
          }} testid="voice-lead-calls-enabled" />
        </Row>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><p className={labelCls}>From (IST)</p><input type="time" className={inputCls + " mt-1"} value={(lc.hours || ["11:00", "19:30"])[0]} onChange={(e) => saveLc({ hours: [e.target.value, (lc.hours || ["11:00", "19:30"])[1]] })} /></div>
          <div><p className={labelCls}>Until (IST)</p><input type="time" className={inputCls + " mt-1"} value={(lc.hours || ["11:00", "19:30"])[1]} onChange={(e) => saveLc({ hours: [(lc.hours || ["11:00", "19:30"])[0], e.target.value] })} /></div>
          <div><p className={labelCls}>Max calls / day</p><input type="number" min={0} max={50} className={inputCls + " mt-1"} defaultValue={lc.daily_limit ?? 10} onBlur={(e) => saveLc({ daily_limit: Number(e.target.value) })} /></div>
          <div><p className={labelCls}>Quiet for (hours)</p><input type="number" min={2} max={168} className={inputCls + " mt-1"} defaultValue={lc.quiet_hours ?? 18} onBlur={(e) => saveLc({ quiet_hours: Number(e.target.value) })} /></div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {DAY_NAMES.map((d, i) => (
            <button key={d} onClick={() => saveLc({ days: days.includes(i) ? days.filter((x) => x !== i) : [...days, i].sort() })}
              className={`text-[10px] px-2 py-1 rounded-md font-medium ${days.includes(i) ? "bg-[#075E54] text-white" : "bg-[#F0F2F5] text-[#8696A0]"}`}>{d}</button>
          ))}
          <span className="text-[11px] text-[#8696A0] ml-2">Max {lc.max_attempts ?? 2} tries per lead, then {lc.cooldown_days ?? 14} days rest after an answered call.</span>
        </div>
        <div className="flex items-center gap-2">
          <button className={btnGhost} onClick={runPreview} disabled={busy} data-testid="voice-lead-calls-preview">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} Who would be called next</button>
          {preview && <span className="text-xs text-[#54656F]">{preview.length} people</span>}
        </div>
        {preview && preview.length > 0 && (
          <div className="text-xs text-[#54656F] space-y-1 max-h-48 overflow-y-auto">
            {preview.map((p) => <p key={p.conversation_id}>{p.name || "—"} · …{p.phone_last4} · try {p.attempt} · score {p.score}</p>)}
          </div>
        )}
        {ov?.calls_last_7_days?.length > 0 && (
          <div className="border-t border-[#E9EDEF] pt-3 text-xs text-[#54656F] space-y-1">
            <p className={labelCls}>Automatic and briefed calls, last 7 days</p>
            {ov.calls_last_7_days.slice(0, 15).map((c) => (
              <p key={c.call_id}>{when(c.created_at)} · …{String(c.to || "").slice(-4)} · {c.status}{c.duration_s ? ` · ${mmss(c.duration_s)}` : ""}{c.call_brief?.goal ? ` · ${c.call_brief.goal.slice(0, 70)}` : ""}</p>
            ))}
          </div>
        )}
      </div>

      <div className={card}>
        <Row label="Redial when a call drops" hint="If the line cuts in the middle of a call, Vivaan rings the caller straight back. OFF since 12 Oct by your rule (the caller calls again).">
          <Toggle on={!!data.settings.auto_callback} onChange={saveCb} testid="voice-auto-callback" />
        </Row>
      </div>
    </div>
  );
}

// ---------------- Logs ----------------
function CallDrawer({ id, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/voice/calls/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);
  const ex = c?.extracted || {};
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="w-full max-w-xl h-full bg-white overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="voice-call-drawer">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg">Call details</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        {!c ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
            <p className="text-xs text-[#8696A0]">{when(c.created_at)} · {c.direction} · {c.channel} · {mmss(c.duration_s)} · {c.end_reason || c.status}</p>
            {c.status === "live" && <button className={btnGhost} onClick={() => api.post(`/voice/calls/${c.call_id}/hangup`).then(() => toast.success("Ending call"))}><PhoneOff className="h-4 w-4" /> End this call</button>}
            {c.has_recording && <audio controls className="w-full" src={`${API_BASE}/voice/calls/${c.call_id}/recording`} />}
            {Object.keys(ex).length > 0 && (
              <div className="border border-[#E9EDEF] rounded-lg p-3 text-sm space-y-1">
                {ex.summary && <p><b>Summary:</b> {ex.summary}</p>}
                {ex.next_step && <p><b>Next step:</b> {ex.next_step}</p>}
                <p className="text-xs text-[#54656F]">{[ex.caller_name, ex.phone, ex.service, ex.quoted_price ? `₹${ex.quoted_price}` : "", ex.intent, ex.deadline].filter(Boolean).join(" · ")}</p>
              </div>
            )}
            {(c.errors || []).length > 0 && <p className="text-xs text-red-600">Problems: {c.errors.join(" | ")}</p>}
            <div className="space-y-2">
              {(c.transcript || []).map((t, i) => (
                <div key={i} className={`flex ${t.role === "user" ? "justify-end" : ""}`}>
                  <p className={`text-sm rounded-lg px-3 py-2 max-w-[85%] ${t.role === "user" ? "bg-[#D9FDD3]" : "bg-[#F0F2F5]"} ${t.interrupted ? "opacity-60" : ""}`}>{t.text}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LogsTab() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    api.get("/voice/calls").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/voice/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["Calls this month", stats.calls], ["Minutes", stats.minutes], ["Leads from calls", stats.leads], ["Live now", stats.live], ["Est. AI cost", `₹${stats.est_cost_inr}`]].map(([k, v]) => (
            <div key={k} className="border border-[#E9EDEF] bg-white rounded-lg p-4"><p className={labelCls}>{k}</p><p className="text-xl mt-1">{v}</p></div>
          ))}
        </div>
      )}
      <div className="border border-[#E9EDEF] bg-white rounded-lg overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm" data-testid="voice-calls-table">
          <thead><tr className="border-b border-[#E9EDEF] font-mono text-[10px] uppercase text-[#8696A0]">
            <th className="px-4 py-3">When</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Caller</th><th className="px-4 py-3">Length</th><th className="px-4 py-3">Result</th><th className="px-4 py-3">Interest</th>
          </tr></thead>
          <tbody className="divide-y divide-[#E9EDEF]">
            {rows === null && <tr><td className="px-4 py-6" colSpan={6}><Loader2 className="h-4 w-4 animate-spin" /></td></tr>}
            {rows?.length === 0 && <tr><td className="px-4 py-6 text-[#8696A0]" colSpan={6}>No calls yet. Try a test call.</td></tr>}
            {rows?.map((c) => (
              <tr key={c.call_id} className="hover:bg-[#F0F2F5] cursor-pointer" onClick={() => setOpen(c.call_id)}>
                <td className="px-4 py-3">{when(c.created_at)}</td>
                <td className="px-4 py-3 flex items-center gap-1">{c.direction === "outbound" ? <PhoneOutgoing className="h-3 w-3" /> : <PhoneIncoming className="h-3 w-3" />}{c.direction} · {c.channel}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.extracted?.caller_name || c.from || c.to || "—"}</td>
                <td className="px-4 py-3 font-mono">{mmss(c.duration_s)}</td>
                <td className="px-4 py-3 text-xs">{c.status === "live" ? <span className="text-[#25D366] font-semibold">● live</span> : (c.end_reason || c.status)}</td>
                <td className="px-4 py-3 text-xs">{c.extracted?.intent || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <CallDrawer id={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

export default function VoiceCalls() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = tab || "agent";
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const load = useCallback(() => {
    api.get("/voice/settings").then((r) => setData(r.data)).catch((e) => toast.error(errText(e, "Could not load voice settings")));
    api.get("/voice/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <PageHeader eyebrow="AI Voice" title="Voice Calls" />
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex gap-1 px-4 md:px-8 pt-4 border-b border-[#E9EDEF] bg-[#F0F2F5] flex-wrap">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`voice-tab-${t.k}`} onClick={() => navigate(`/voice/${t.k}`)}
              className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${active === t.k ? "border-[#25D366] text-[#111B21]" : "border-transparent text-[#8696A0] hover:text-[#374151]"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="p-4 md:p-8">
          {!data || !catalog ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <>
              {active === "agent" && <AgentTab data={data} catalog={catalog} reload={load} />}
              {active === "test" && <TestTab data={data} />}
              {active === "widget" && <WidgetTab data={data} reload={load} />}
              {active === "phone" && <PhoneTab data={data} reload={load} />}
              {active === "automations" && <CallAutomationsTab data={data} reload={load} />}
              {active === "logs" && <LogsTab />}
            </>
          )}
        </div>
      </div>
    </>
  );
}
